/* eslint-disable max-lines */
import React, { PropsWithChildren } from "react";
import styled, { css } from "styled-components";
import { Box, Flex } from "grid-styled";
import { GlobalsContainer, IconButton, shadows, ThemeName, transition } from "@evergis/ui";
import { IIconButtonProps } from "@evergis/ui/dist/molecules/IconButton/types";

import { LegendToggler } from "../../modules/LayerList/styled";
import { LEFT_PANEL_MIN_WIDTH } from "./constants";
import { ContainerProps, PanelContainerProps } from "./types";

export const PanelWidthDragButton = styled(IconButton)`
  z-index: 10;
  cursor: grab;
  position: absolute;
  top: 50%;
  right: -0.5rem;
  margin-top: -1rem;
`;
PanelWidthDragButton.defaultProps = {
  kind: "dragdrop",
};

export const PanelContainer = styled((props: PropsWithChildren<PanelContainerProps>) => <Box {...props} />)`
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: ${({ theme: { palette } }) => palette.background};
  transition: margin-left ${transition.show};
  box-shadow: ${shadows.raised};
  z-index: 7;
`;

export const BackToLayers = styled(LegendToggler)`
  width: auto;

  &&& > * {
    font-size: 0.75rem;
  }

  span[kind] {
    display: none;

    :after {
      color: ${({ theme: { palette } }) => palette.primary};
    }
  }

  :hover span[kind] {
    display: block;
  }
`;

export const PanelContent = styled.div<{ isResizing?: boolean }>`
  display: ${({ isResizing }) => (isResizing ? "none" : "flex")};
  flex-direction: column;
  width: 100%;
  height: 100%;
  position: relative;
  z-index: 7;
`;

const BottomLayoutChildren = styled.div`
  > * {
    pointer-events: auto;
  }
`;

export const PanelBlock = styled(BottomLayoutChildren)`
  height: 2.5rem; // auto if vertical
  width: auto; // 2.5 rem if vertical
  display: flex;
  flex-direction: row; //change for different positions
`;

export const AdditionalPanel = styled(BottomLayoutChildren)`
  > * {
    pointer-events: auto;
  }
`;

export const BottomLayout = styled.div<{
  leftWidth?: number;
  bottomOpened?: boolean;
  bottomHeight?: number;
  themeName?: ThemeName;
  isPresentationMode?: boolean;
}>`
  display: flex;
  flex-direction: column;
  position: absolute;
  padding: 0.75rem;
  width: calc(100% - ${({ leftWidth }) => leftWidth || 0}px);
  box-sizing: border-box;
  bottom: ${({ bottomOpened, bottomHeight }) => (bottomOpened ? `${bottomHeight}px` : 0)};
  left: ${({ leftWidth }) => leftWidth || 0}px;
  pointer-events: none;

  ${({ isPresentationMode }) =>
    isPresentationMode &&
    css`
      ${PanelBlock} {
        justify-content: center;
      }

      ${AdditionalPanel} {
        align-self: center;
        width: calc(100% - ${LEFT_PANEL_MIN_WIDTH * 2}px);
      }
    `};
`;

export const TopPanelBlock = styled(PanelBlock)<{
  leftWidth?: number;
  isPresentationMode?: boolean;
}>`
  z-index: 98;
  position: absolute;
  left: ${({ leftWidth }) => leftWidth || 0}px;
  top: 0;
  bottom: unset;
  height: auto;
  padding: 0.75rem;

  ${({ leftWidth, isPresentationMode }) =>
    isPresentationMode &&
    css`
      width: calc(100% - ${leftWidth || 0}px - 1.5rem);
      justify-content: center;
    `};
`;

export const LayoutContainer = styled(Flex)`
  height: 100%;
  width: 100vw;
  overflow: hidden;
  position: relative;
`;

export const LeftContainer = styled(Flex)<{ $isHidden?: boolean }>`
  z-index: 4;
  flex-direction: row;
  position: relative;
  height: 100%;
  margin-left: ${({ $isHidden }) => ($isHidden ? -LEFT_PANEL_MIN_WIDTH : 0)}px;
  transition: margin-left ${transition.collapse};
`;

export const GlobalsContainerStyled = styled(GlobalsContainer)`
  height: 100%;
  overflow: hidden;
`;

export const MainContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const Top = styled.div<ContainerProps>`
  position: relative;
  width: 100%;
  background-color: ${({ theme: { palette } }) => palette.elementDark};

  &[data-bottom="none"] {
    height: 100%;
  }

  &[data-bottom="opened"] {
    height: 100%;
  }

  &[data-bottom="fullscreen"] {
    display: none;
  }
`;

export const Bottom = styled.div<ContainerProps>`
  width: 100%;
  background-color: ${({ theme: { palette } }) => palette.background};
  overflow: auto;
  z-index: 1;

  &[data-bottom="none"] {
    display: none;
  }

  &[data-bottom="opened"] {
    position: absolute;
    bottom: 0;
    height: 350px;
    max-height: 350px;
    background-color: ${({ theme: { palette } }) => palette.background};
  }

  &[data-bottom="fullscreen"] {
    height: 100%;
    max-height: 100vh;
  }
`;

export const NirvanaButton = styled(IconButton).attrs({
  kind: "next",
})<IIconButtonProps & { $bottomHeight?: number; $isNirvanaMode?: boolean }>`
  z-index: 1;
  position: absolute;
  width: 0.75rem;
  height: 2.5rem;
  right: -0.75rem;
  top: calc(50% - ${({ $bottomHeight }) => $bottomHeight / 2 || 0}px - 1.25rem);
  background-color: #3d3d3d;
  border-radius: ${({ theme: { borderRadius } }) => `0 ${borderRadius.xSmall} ${borderRadius.xSmall} 0`};
  transition: opacity ${transition.hover};
  opacity: 0.36;

  span[kind]:after {
    top: 0.125rem;
    font-size: 0.75rem;
    color: #fff;
    transform: ${({ $isNirvanaMode }) => `rotate(${$isNirvanaMode ? 0 : 180}deg)`};
    transition: transform 300ms cubic-bezier(0.15, 1.35, 0.7, 1.5);
  }

  :hover {
    opacity: 0.8;
  }
`;
